import { useQuery } from '@tanstack/react-query'
import { getSupabaseUrl, getSupabaseAnonKey } from '@/lib/runtime-env'

/**
 * One Linear release as returned by the `release-notes` edge function (recent
 * mode). Not an app domain type — it mirrors the function's payload shape.
 */
export interface LinearReleaseNotes {
  version: string
  name: string | null
  url: string | null
  completedAt: string | null
  /** Markdown body of the Linear release note; null when the release has none. */
  notes: string | null
  notesUrl: string | null
}

/** Query-key factory for the release-notes domain. */
export const releaseNotesKeys = {
  recent: (version: string | null) => ['release-notes', 'recent', version] as const,
}

/**
 * Fetches the recent Linear releases (newest first, ≤10) via the `release-notes`
 * edge function — the "What's new" list. `version` caps the list to this
 * build's tag (nothing newer than what's deployed); pass `null` to fetch the
 * uncapped list (e.g. a local `dev` build with no semver tag).
 *
 * Uses a raw `fetch` against the RUNTIME Supabase URL (window.__ENV__ / config.js),
 * NOT `supabase.functions.invoke` / `import.meta.env` — the latter is undefined
 * in the prod image and yields a broken relative URL (mirrors AboutBuildModal's
 * prior fetch and useLedgerSync). The function is public (verify_jwt=false) but
 * the anon key is still sent so the Supabase gateway accepts the request.
 */
async function fetchRecentReleases(version: string | null): Promise<LinearReleaseNotes[]> {
  const anonKey = getSupabaseAnonKey()
  const response = await fetch(`${getSupabaseUrl()}/functions/v1/release-notes`, {
    method: 'POST',
    headers: {
      apikey: anonKey,
      Authorization: `Bearer ${anonKey}`,
      'Content-Type': 'application/json',
    },
    // `version` is omitted (not null) when there's no valid semver tag, so the
    // function returns the uncapped list instead of rejecting a bad cap (400).
    body: JSON.stringify(version ? { recent: true, version } : { recent: true }),
  })
  if (!response.ok) {
    throw new Error(`Failed to load release notes: ${response.status}`)
  }
  const json = (await response.json()) as { releases?: LinearReleaseNotes[] } | null
  return json?.releases ?? []
}

/**
 * Recent Linear releases for the "What's new" list, capped at the deployed
 * `version`. Fetched lazily — `enabled` is wired to the dialog being open.
 * Cosmetic data: no retries and no toast on error; the dialog shows a quiet
 * "no notes" line instead.
 *
 * @param version - v-prefixed build tag to cap at, or null for uncapped
 * @param enabled - whether to fetch (true once the dialog opens)
 */
export function useReleaseNotes(version: string | null, enabled: boolean) {
  return useQuery({
    queryKey: releaseNotesKeys.recent(version),
    queryFn: () => fetchRecentReleases(version),
    enabled,
    retry: false,
  })
}
